import { useCallback, useMemo, useRef, useState } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { F0Text } from '@factorialco/f0-react'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import {
  type PlanningBalancesByPeriodsQuery,
  type PlanningBalancesQuery,
} from 'generated/resources/graphql'
import moment from 'moment'

import { i18nDateToString, type FormatTypes } from 'modules/core/lib/dates'
import { TimeGridTable } from 'modules/shift_management/components/ShiftManagement/TimeGridTable/index'

import type { ShiftsRow, View } from 'modules/shift_management/lib/types'
import { resolveDimension } from 'modules/shift_management/utils/TimeGridTable/dimensions'
import { formatHeader } from 'modules/shift_management/utils/TimeGridTable/buildHeader'
import { buildTimeSlots } from 'modules/shift_management/utils/TimeGridTable/slots'

import { FirstCell } from 'modules/shift_management/components/ShiftManagement/Content/Body/DataTable/Row/FirstCell'
import type {
  GridSelection,
  TimeGranularity,
  TimeSlot,
} from 'modules/shift_management/components/ShiftManagement/TimeGridTable/types'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import { useBuildEvents } from 'modules/shift_management/hooks/gridTable/useBuildEvents'
import { useBuildTimeOffSummary } from 'modules/shift_management/hooks/gridTable/useBuildTimeOffSummary'
import { resolveCard } from 'modules/shift_management/utils/TimeGridTable/resolveCard'
import i18n from 'modules/core/lib/i18n'
import SortEmployeesButton from 'modules/shift_management/components/ShiftManagement/Content/Body/ShiftManagementTimeGrid/SortEmployeesButton'
import useGetTimeOffRow from 'modules/shift_management/components/ShiftManagement/Content/Body/DataTable/TimeOffRow/useGetTimeOffRow'
import TimeOffSidepanel from 'modules/shift_management/components/ShiftManagement/TimeOffSidepanel'
import { useGridClipboardActions } from 'modules/shift_management/hooks/gridTable/useGridClipboardActions'
import { useGridDragAndDropAdapter } from 'modules/shift_management/hooks/gridTable/useGridDragAndDropAdapter'
import { useSelectionDispatch } from 'modules/shift_management/hooks/useSelection'
import { useEnabledFeatures } from 'modules/shift_management/hooks/useEnabledFeatures'
import { usePermissions } from 'modules/shift_management/hooks/usePermissions'
import { useCoverageColumns } from 'modules/shift_management/components/ShiftManagement/Content/Body/ShiftManagementTimeGrid/useCoverageColumns'
import CoverageSection from 'modules/shift_management/components/ShiftManagement/Content/Body/DataTable/CoverageSection/index'
import useMoveShift from 'modules/shift_management/components/ShiftManagement/Content/Body/DataTable/utils/useMoveShift'
import useFetchDefaultShifts from 'modules/shift_management/components/ShiftManagement/MainPage/useFetchDefaultShifts'
import QuickActionsPopover from 'modules/shift_management/components/ShiftManagement/QuickActionsPopover'
import { fromCellKey } from 'modules/shift_management/utils/TimeGridTable/cellCoordinates'
import type { DaysByRow } from 'modules/shift_management/hooks/reducers/selection'
import type {
  ShiftGridEvent,
  RestDayGridEvent,
  ExtendedGridEventAppearance,
} from 'modules/shift_management/utils/TimeGridTable/transformEvents'
import type { Shift, RestDay, UnassignedShift } from 'modules/shift_management/lib/types'
import { trackAddShiftStartEvent } from 'modules/shift_management/lib/trackEvents'

interface BuildDaysByRowDeps {
  events: ExtendedGridEventAppearance[]
  entities: { id: string; selectable?: boolean }[]
  dateFormat: FormatTypes
  isSelectableDayForGridSelection: (rowKey: string, date: moment.Moment) => boolean
}

const buildDaysByRow = (
  selection: Exclude<GridSelection, { kind: 'none' }>,
  gridSlots: TimeSlot[],
  { events, entities, dateFormat, isSelectableDayForGridSelection }: BuildDaysByRowDeps
): DaysByRow => {
  const daysByRow: DaysByRow = {}

  if (selection.kind === 'all') {
    const slotDates = gridSlots.map((slot) => moment(slot.start))
    for (const entity of entities) {
      if (entity.id === TIME_OFF_ENTITY_ID) continue
      if (entity.selectable === false) continue
      const selectedDays = slotDates
        .filter((date) => isSelectableDayForGridSelection(entity.id, date))
        .map((date) => i18nDateToString(date, dateFormat))

      if (selectedDays.length > 0) {
        daysByRow[entity.id] = selectedDays
      }
    }
  } else if (selection.kind === 'event') {
    // Multi-event selections (Cmd/Ctrl+click) are translated into global/header
    // selection so bulk actions (Add/Delete/Edit) work from the top bar.
    for (const key of selection.keys) {
      const separatorIndex = key.indexOf(':')
      if (separatorIndex === -1) continue
      const entityId = key.substring(0, separatorIndex)
      const eventId = key.substring(separatorIndex + 1)

      if (entityId === TIME_OFF_ENTITY_ID) continue

      const event = events.find((e) => e.id === eventId && e.entityId === entityId)
      if (!event) continue

      if (event.type !== 'shift' && event.type !== 'rest_day') continue

      const date = moment(event.start)
      if (!isSelectableDayForGridSelection(entityId, date)) continue

      const dateStr = i18nDateToString(date, dateFormat)
      const existing = daysByRow[entityId] ?? []
      if (!existing.includes(dateStr)) {
        existing.push(dateStr)
      }
      daysByRow[entityId] = existing
    }
  } else if (selection.kind === 'cells') {
    for (const key of selection.keys) {
      const { entityId, slotIndex } = fromCellKey(key)
      if (entityId === TIME_OFF_ENTITY_ID) continue
      const slot = gridSlots[slotIndex]
      if (!slot) continue
      const date = moment(slot.start)
      if (!isSelectableDayForGridSelection(entityId, date)) continue
      const dateStr = i18nDateToString(date, dateFormat)
      const existing = daysByRow[entityId] ?? []
      existing.push(dateStr)
      daysByRow[entityId] = existing
    }
  } else if (selection.kind === 'row') {
    if (selection.rowId === TIME_OFF_ENTITY_ID) return daysByRow
    const selectedDays = gridSlots
      .map((slot) => moment(slot.start))
      .filter((date) => isSelectableDayForGridSelection(selection.rowId, date))
      .map((date) => i18nDateToString(date, dateFormat))

    if (selectedDays.length > 0) {
      daysByRow[selection.rowId] = selectedDays
    }
  } else if (selection.kind === 'column') {
    const slot = gridSlots[selection.columnIndex]
    if (!slot) return daysByRow
    const date = moment(slot.start)
    const dateStr = i18nDateToString(date, dateFormat)
    for (const entity of entities) {
      if (entity.id === TIME_OFF_ENTITY_ID) continue
      if (!isSelectableDayForGridSelection(entity.id, date)) continue
      daysByRow[entity.id] = [dateStr]
    }
  }

  return daysByRow
}

const collectShiftsForSelection = (
  daysByRow: DaysByRow,
  events: ExtendedGridEventAppearance[],
  dateFormat: FormatTypes
): Record<string, Shift> => {
  const shifts: Record<string, Shift> = {}
  for (const event of events) {
    if (event.type !== 'shift') continue
    const shiftEvent: ShiftGridEvent = event
    const selectedDays = daysByRow[shiftEvent.entityId]
    if (!selectedDays) continue
    const date = moment(shiftEvent.start)
    const dateStr = i18nDateToString(date, dateFormat)
    if (!selectedDays.includes(dateStr)) continue
    const shift = shiftEvent.shift
    shifts[shift.referenceKey(date)] = shift
  }
  return shifts
}

const collectRestDaysForSelection = (
  daysByRow: DaysByRow,
  events: ExtendedGridEventAppearance[],
  dateFormat: FormatTypes
): Record<string, RestDay> => {
  const restDays: Record<string, RestDay> = {}
  for (const event of events) {
    if (event.type !== 'rest_day') continue
    const restDayEvent: RestDayGridEvent = event
    const selectedDays = daysByRow[restDayEvent.entityId]
    if (!selectedDays) continue
    const date = moment(restDayEvent.start)
    const dateStr = i18nDateToString(date, dateFormat)
    if (!selectedDays.includes(dateStr)) continue
    const restDay = restDayEvent.restDay
    restDays[restDay.id] = restDay
  }
  return restDays
}

// Number of elements preloaded before and after the visible area
const BASIC_VERSION_OFFSET = 5
const FULL_VERSION_OFFSET = 10
const TIME_OFF_ENTITY_ID = '__time_off__'
interface Props {
  view: View
  shiftsRows: ShiftsRow[]
  balancesByEmployee: Record<string, TimePlanningBalance>
  periodBalancesByEmployee: Record<string, TimePlanningBalancesByPeriod[]>
}

type TimePlanningBalance = NonNullable<
  PlanningBalancesQuery['timePlanning']['balancesConnection']['nodes']
>[number]
type TimePlanningBalancesByPeriod = NonNullable<
  PlanningBalancesByPeriodsQuery['timePlanning']['balancesByPeriodsConnection']['nodes']
>[number]

const ShiftManagementTimeGrid = ({
  view,
  shiftsRows,
  balancesByEmployee,
  periodBalancesByEmployee,
}: Props) => {
  const [{ shiftsConflicts }] = useSchedule()
  const [isTimeOffSidePanelOpen, setIsTimeOffSidePanelOpen] = useState(false)
  const [selectedTimeOffDate, setSelectedTimeOffDate] = useState<moment.Moment>(moment())
  const granularity: TimeGranularity = view.granularity
  const rowHeaderWidth = resolveDimension('s240', 240)
  const minColumnWidth = resolveDimension(view.columnMinWidth, 80)
  const rowMinHeight = resolveDimension(view.cellMinHeight, 48)
  const shouldShowTimeOffRow = view.isByAreas

  const timeRange = useUnsafeMemo(
    () => ({ start: view.firstDay.toDate(), end: view.lastDay.toDate() }),
    [view.firstDay, view.lastDay]
  )

  const preloadedRowsOutsideViewport = useUnsafeMemo(() => {
    return view.isFullVersion ? FULL_VERSION_OFFSET : BASIC_VERSION_OFFSET
  }, [view.isFullVersion])

  const {
    leaves,
    restDays,
    holidays,
    loading: isTimeOffLoading,
  } = useGetTimeOffRow({
    skip: !shouldShowTimeOffRow,
  })

  const slots = useUnsafeMemo(() => {
    if (!shouldShowTimeOffRow) return []
    return buildTimeSlots({ timeRange, granularity }).slots
  }, [granularity, shouldShowTimeOffRow, timeRange])

  const timeOffSummary = useBuildTimeOffSummary({
    shouldShow: shouldShowTimeOffRow,
    slots,
    entityId: TIME_OFF_ENTITY_ID,
    data: {
      holidays,
      leaves,
      restDays,
      loading: isTimeOffLoading,
    },
    onOpenSidepanel: (date) => {
      setSelectedTimeOffDate(date)
      setIsTimeOffSidePanelOpen(true)
    },
  })

  const events = useBuildEvents({
    shiftsRows,
    view,
    shiftsConflicts,
    timeOffSummary,
  })

  const shiftsRowsByKey = useUnsafeMemo(
    () => new Map(shiftsRows.map((row) => [row.key, row])),
    [shiftsRows]
  )

  const entities = useUnsafeMemo(() => {
    const baseEntities = shiftsRows.map((row) => {
      if (!view.isByEmployees) return { id: row.key, selectable: true }
      return { id: row.key, selectable: row.canManage }
    })
    if (!shouldShowTimeOffRow) return baseEntities

    return [{ id: TIME_OFF_ENTITY_ID, selectable: false }, ...baseEntities]
  }, [shouldShowTimeOffRow, shiftsRows, view.isByEmployees])

  const shouldEnableClipboardInTable = !view.isByAreas
  const clipboardHandlers = useGridClipboardActions({
    canUseClipboardActions: true,
    shiftsRows,
  })
  const { moveShift } = useMoveShift()

  const handleDropUnassignedShift = useCallback(
    async ({
      unassignedShift,
      targetEntityId,
      targetDate,
    }: {
      unassignedShift: UnassignedShift
      targetEntityId: string
      targetDate: moment.Moment
    }) => {
      const targetRow = shiftsRowsByKey.get(targetEntityId)
      if (!targetRow) return

      const employeeId = targetRow.id
      const formattedDate = i18nDateToString(targetDate, 'backend')
      const daysByEmployee: Record<string, string[]> = {
        [String(employeeId)]: [formattedDate],
      }

      await moveShift({
        newShiftsRow: targetRow,
        unassignedShift,
        date: targetDate,
        daysByEmployee,
        employeeId,
      })
    },
    [moveShift, shiftsRowsByKey]
  )

  const dragAndDropAdapter = useGridDragAndDropAdapter({
    clipboardHandlers,
    view,
    onDropUnassignedShift: handleDropUnassignedShift,
  })

  const [{ canUseCoverageView }] = useEnabledFeatures()
  const [{ canManageUnassignedShifts }] = usePermissions()
  const canViewCoverageSection =
    canUseCoverageView && canManageUnassignedShifts && view.isByEmployees

  const [hover, setHover] = useState(false)
  const [isQuickActionsOpen, setIsQuickActionsOpen] = useState(false)
  const [quickActionsAnchorRect, setQuickActionsAnchorRect] = useState<DOMRect | null>(null)
  const [ghostAnchorRect, setGhostAnchorRect] = useState<DOMRect | null>(null)
  const [quickActionsSelection, setQuickActionsSelection] = useState<{
    entityId: string
    slotIndex: number
  } | null>(null)

  const resizeRef = useRef<HTMLDivElement>(null)
  const coverageColumns = useCoverageColumns(view)
  const { defaultShifts: visibleDefaultShifts, loading: isDefaultShiftsLoading } =
    useFetchDefaultShifts({})

  const dispatchSelection = useSelectionDispatch()

  const hasApprovedNonWorkableFullDayLeave = useCallback(
    (rowKey: string, date: moment.Moment) => {
      const row = shiftsRowsByKey.get(rowKey)
      if (!row) return false

      const dayLeaves = row.leaves.filterByDate(date)
      return dayLeaves.some(
        (leave) => !leave.workable && !leave.hourly && !leave.halfDay && leave.approved
      )
    },
    [shiftsRowsByKey]
  )

  const isShiftManagementPlanningDate = useCallback(
    (rowKey: string, date: moment.Moment) => {
      if (!view.isByEmployees) return true

      const row = shiftsRowsByKey.get(rowKey)
      if (!row) return false

      return !!row.planningVersions.planningVersionWithinDate(row.id, date)?.isShiftManagementTool
    },
    [shiftsRowsByKey, view.isByEmployees]
  )

  const isSelectableDayForGridSelection = useCallback(
    (rowKey: string, date: moment.Moment) => {
      if (!isShiftManagementPlanningDate(rowKey, date)) return false
      return !hasApprovedNonWorkableFullDayLeave(rowKey, date)
    },
    [hasApprovedNonWorkableFullDayLeave, isShiftManagementPlanningDate]
  )

  // Single-event selection is card-local only (opens the card actions menu)
  // and intentionally does NOT sync to global/header selection. Multi-event
  // selections (Cmd/Ctrl+click) DO sync so that bulk actions work from the top bar.
  const isSingleEventCardSelection = (selection: GridSelection): boolean =>
    selection.kind === 'event' && selection.keys.size === 1

  const handleSelectionChange = useCallback(
    (selection: GridSelection, gridSlots: TimeSlot[]) => {
      if (selection.kind === 'none') {
        dispatchSelection({ type: 'CLEAR_SELECTION' })
        return
      }
      if (isSingleEventCardSelection(selection)) {
        dispatchSelection({ type: 'CLEAR_SELECTION' })
        return
      }

      const daysByRow = buildDaysByRow(selection, gridSlots, {
        events,
        entities,
        dateFormat: view.dateFormat,
        isSelectableDayForGridSelection,
      })

      if (Object.keys(daysByRow).length === 0) {
        dispatchSelection({ type: 'CLEAR_SELECTION' })
        return
      }

      const selectedShifts = collectShiftsForSelection(daysByRow, events, view.dateFormat)
      const selectedRestDays = collectRestDaysForSelection(daysByRow, events, view.dateFormat)

      dispatchSelection({
        type: 'SET_FROM_GRID',
        daysByRow,
        shifts: selectedShifts,
        restDays: selectedRestDays,
      })
    },
    [dispatchSelection, entities, events, isSelectableDayForGridSelection, view.dateFormat]
  )

  const handleAddShiftClick = useCallback(
    (entity: { id: string }, slotIndex: number, anchorRect: DOMRect) => {
      setIsQuickActionsOpen(true)
      setQuickActionsSelection({ entityId: entity.id, slotIndex })
      setQuickActionsAnchorRect(anchorRect)
      setGhostAnchorRect(null)
      dispatchSelection({ type: 'SHOW_EMPTY_SHIFT_CELL' })

      const targetRow = shiftsRowsByKey.get(entity.id)
      if (!targetRow) return

      const slot = buildTimeSlots({ timeRange, granularity }).slots[slotIndex]
      if (!slot) return

      dispatchSelection({
        type: 'TOGGLE_DAY',
        day: moment(slot.start),
        shiftsRow: targetRow,
        view,
        forcedValue: true,
      })

      trackAddShiftStartEvent({
        view,
        impactedEmployees: 1,
        cellCount: 1,
        shiftType: 'savedCustom',
        from: 'contextual_button',
        quickActions: true,
      })
    },
    [dispatchSelection, granularity, shiftsRowsByKey, timeRange, view]
  )

  const selectedShiftRow = useMemo(() => {
    if (!quickActionsSelection) return null
    return shiftsRowsByKey.get(quickActionsSelection.entityId) ?? null
  }, [quickActionsSelection, shiftsRowsByKey])

  const selectedDate = useMemo(() => {
    if (!quickActionsSelection) return null
    const slot = buildTimeSlots({ timeRange, granularity }).slots[quickActionsSelection.slotIndex]
    if (!slot) return null
    return moment(slot.start)
  }, [granularity, quickActionsSelection, timeRange])

  const closeQuickActions = useCallback(() => {
    setIsQuickActionsOpen(false)
    setQuickActionsSelection(null)
    setQuickActionsAnchorRect(null)
    setGhostAnchorRect(null)
    dispatchSelection({ type: 'HIDE_EMPTY_SHIFT_CELL' })
    dispatchSelection({ type: 'CLEAR_SELECTION' })
  }, [dispatchSelection])

  const handleGhostCardRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (!isQuickActionsOpen || !node) return
      const nextRect = node.getBoundingClientRect()

      setGhostAnchorRect((prevRect) => {
        if (!prevRect) return nextRect

        const samePosition =
          Math.abs(prevRect.left - nextRect.left) < 0.5 &&
          Math.abs(prevRect.top - nextRect.top) < 0.5 &&
          Math.abs(prevRect.width - nextRect.width) < 0.5 &&
          Math.abs(prevRect.height - nextRect.height) < 0.5

        return samePosition ? prevRect : nextRect
      })
    },
    [isQuickActionsOpen]
  )

  const forcedQuickActionsAnchorRect = useMemo(() => {
    if (!isQuickActionsOpen || !quickActionsSelection) return null
    return ghostAnchorRect ?? quickActionsAnchorRect
  }, [ghostAnchorRect, isQuickActionsOpen, quickActionsAnchorRect, quickActionsSelection])

  return (
    <>
      <TimeGridTable
        entities={entities}
        events={events}
        timeRange={timeRange}
        granularity={granularity}
        rowHeaderLabel={view.isByAreas ? 'Work area' : <SortEmployeesButton />}
        rowHeaderWidth={rowHeaderWidth}
        minColumnWidth={minColumnWidth}
        rowMinHeight={rowMinHeight}
        paddingX={4}
        paddingY={4}
        cardLayout={{
          minHeight: rowMinHeight,
          paddingX: 6,
          paddingY: 4,
          radius: 8,
        }}
        preloadedRowsOutsideViewport={preloadedRowsOutsideViewport}
        timeSlotFormatter={(slot) => formatHeader(slot.start, view, view.isTimeFormat24H)}
        rowHeaderRenderer={(entity) => {
          if (entity.id === TIME_OFF_ENTITY_ID) {
            return (
              <Box width="full" justifyContent="center" paddingX="s16">
                <F0Text
                  variant="label"
                  content={i18n.t('shift_management.time_off_row.title')}
                  ellipsis
                />
              </Box>
            )
          }

          const row = shiftsRowsByKey.get(entity.id)
          if (!row) return null

          return (
            <FirstCell
              view={view}
              shiftsRow={row}
              balances={balancesByEmployee[row.key]}
              periodBalances={periodBalancesByEmployee[row.key]}
            />
          )
        }}
        cardAppearance={(event) => resolveCard(event.type).getAppearance(event)}
        eventRenderer={(event, context) => resolveCard(event.type).render(event, context)}
        dragAndDropAdapter={dragAndDropAdapter}
        clipboardHandlers={shouldEnableClipboardInTable ? clipboardHandlers : undefined}
        onSelectionChange={handleSelectionChange}
        onAddShiftClick={handleAddShiftClick}
        ghostCell={isQuickActionsOpen ? quickActionsSelection : null}
        onGhostCardRef={handleGhostCardRef}
      >
        {canViewCoverageSection && (
          <Box
            justifyContent="flexEnd"
            width="full"
            style={{ position: 'sticky', bottom: '0', zIndex: '1', marginTop: 'auto' }}
          >
            <Box className="bg-f1-background" ref={resizeRef}>
              <CoverageSection
                columns={coverageColumns}
                view={view}
                hover={hover}
                setHover={setHover}
                resizeRef={resizeRef}
              />
            </Box>
          </Box>
        )}
      </TimeGridTable>
      {isQuickActionsOpen &&
        quickActionsSelection &&
        forcedQuickActionsAnchorRect &&
        selectedShiftRow &&
        selectedDate && (
          <QuickActionsPopover
            date={selectedDate}
            shiftsRow={selectedShiftRow}
            visibleDefaultShifts={visibleDefaultShifts}
            isDefaultShiftsLoading={isDefaultShiftsLoading}
            forceOpen
            forceAnchorRect={forcedQuickActionsAnchorRect}
            onCloseForced={closeQuickActions}
          />
        )}
      {isTimeOffSidePanelOpen ? (
        <TimeOffSidepanel
          open={isTimeOffSidePanelOpen}
          onClose={() => setIsTimeOffSidePanelOpen(false)}
          selectedDate={selectedTimeOffDate}
          leaves={leaves}
          restDays={restDays}
          holidays={holidays}
        />
      ) : null}
    </>
  )
}

export { ShiftManagementTimeGrid }
